import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Menu, X, Shield, ArrowRight, Play, Clock, Users, BarChart3,
  CheckCircle, Building2, Calendar, FileText, DollarSign, UserCog,
  Lock, Eye, EyeOff, MapPin, Smartphone, Globe, ChevronDown, ChevronUp,
  Mail, Phone, MessageSquare, Send
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Navigation items
const navItems = [
  { label: 'Features', href: '#features' },
  { label: 'Modules', href: '#modules' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

// Stats for hero
const stats = [
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '24/7', label: 'Shift Support' },
  { value: '100%', label: 'PH Compliant' },
  { value: '0', label: 'Hidden Surveillance' },
];

// Features data
const features = [
  {
    icon: Building2,
    title: 'Multi-Company Portfolio',
    description: 'Manage multiple companies from a single owner dashboard with unified insights'
  },
  {
    icon: Clock,
    title: '24/7 Shift Support',
    description: 'Overnight shift detection, flexible scheduling, and break management'
  },
  {
    icon: Calendar,
    title: 'Smart Attendance',
    description: 'GPS-verified clock-in/out with grace periods and late tracking'
  },
  {
    icon: FileText,
    title: 'Leave Management',
    description: 'Role-aware approval routing with balance tracking and accrual rules'
  },
  {
    icon: DollarSign,
    title: 'PH-Compliant Payroll',
    description: 'SSS, PhilHealth, Pag-IBIG, and BIR withholding tax calculations'
  },
  {
    icon: UserCog,
    title: 'Role-Based Access',
    description: 'Owner, Admin, Manager, Employee with granular permissions'
  },
];

// Modules data
const modules = [
  { name: 'Attendance', stats: '99.9% accuracy', icon: Clock },
  { name: 'Leave Requests', stats: 'Auto-routing', icon: Calendar },
  { name: 'OT Pre-Approval', stats: 'Workflow-based', icon: FileText },
  { name: 'Holiday Management', stats: 'PH holidays', icon: Calendar },
  { name: 'PH Payroll', stats: 'BIR compliant', icon: DollarSign },
  { name: 'Audit Logs', stats: 'GPS tracking', icon: MapPin },
];

// FAQ data
const faqs = [
  {
    q: 'How does OT pre-approval work?',
    a: 'Employees submit OT requests before working overtime. Managers approve/reject with optional partial approval. Only approved OT is included in payroll calculations.'
  },
  {
    q: 'Can managers see employee salaries?',
    a: 'No. Compensation data is strictly restricted to Owner and Admin roles. Managers can only see attendance hours and team performance metrics.'
  },
  {
    q: 'How are 24/7 shifts handled?',
    a: 'The system automatically detects overnight shifts (e.g., 10PM-6AM) and correctly calculates hours across day boundaries.'
  },
  {
    q: 'What Philippine holidays are supported?',
    a: 'All regular and special non-working holidays with correct pay multipliers (200% for regular, 130% for special).'
  },
  {
    q: 'Is GPS tracking always on?',
    a: 'GPS is captured only at clock-in and clock-out moments. There is no continuous location tracking or surveillance.'
  },
];

// Navbar Component
const Navbar = ({ isScrolled, logoUrl, appName }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? 'bg-white/90 backdrop-blur-lg shadow-sm border-b border-slate-200/50'
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-xl" />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">{(appName || 'W')[0]}</span>
              </div>
            )}
            <span className={`font-bold text-xl ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
              {appName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isScrolled
                    ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    : 'text-slate-300 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isScrolled
                  ? 'text-slate-600 hover:text-slate-900'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Sign In
            </button>
            <a
              href="#contact"
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-blue-500/25"
            >
              Request Demo
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg ${isScrolled ? 'text-slate-600' : 'text-white'}`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white rounded-2xl shadow-xl mt-2 p-4 border border-slate-200">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg font-medium"
              >
                {item.label}
              </a>
            ))}
            <div className="border-t border-slate-200 mt-3 pt-3 space-y-2">
              <button
                onClick={() => { setIsMobileMenuOpen(false); navigate('/login'); }}
                className="block w-full text-center px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg font-medium"
              >
                Sign In
              </button>
              <a
                href="#contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-center px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold"
              >
                Request Demo
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const Hero = () => {
  const navigate = useNavigate();
  
  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-slate-300 font-medium">Privacy-First Workforce Management</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
            Owner-grade{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              workforce
            </span>
            {' '}command system
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Unify attendance, approvals, PH payroll, and audit trails—
            <span className="text-white font-medium">without invasive monitoring</span>. 
            Built for Philippine SMEs running 24/7 operations.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a
              href="#contact"
              className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all shadow-2xl shadow-blue-500/30 flex items-center justify-center gap-2"
            >
              Request a Demo
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <button
              onClick={() => navigate('/login')}
              className="group w-full sm:w-auto px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Try Demo Account
            </button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="#f8fafc"
          />
        </svg>
      </div>
    </section>
  );
};

// Features Section
const Features = () => (
  <section id="features" className="py-20 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Everything you need to run your workforce
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          A complete operating system designed for Philippine businesses with 24/7 operations
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {features.map((feature, index) => (
          <div
            key={index}
            className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <feature.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
            <p className="text-slate-600">{feature.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Modules Section
const Modules = () => (
  <section id="modules" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Integrated Modules
        </h2>
        <p className="text-lg text-slate-600">
          All the tools you need, working together seamlessly
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {modules.map((module, index) => (
          <div
            key={index}
            className="bg-slate-50 p-6 rounded-xl border border-slate-200 text-center hover:bg-blue-50 hover:border-blue-200 transition-all"
          >
            <module.icon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1">{module.name}</h3>
            <p className="text-xs text-slate-500">{module.stats}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Privacy Section
const Privacy = () => (
  <section className="py-20 bg-slate-900 text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Privacy by Design
        </h2>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
          We believe in transparency. Here's exactly what we do and don't do.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-6">
          <h3 className="flex items-center gap-2 text-xl font-semibold text-green-400 mb-4">
            <CheckCircle className="w-6 h-6" />
            What We Capture
          </h3>
          <ul className="space-y-3">
            {[
              'Clock-in/out timestamps',
              'GPS at clock events only',
              'Device/IP for audit trail',
              'Approved work hours',
              'Leave and request history'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-green-400" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6">
          <h3 className="flex items-center gap-2 text-xl font-semibold text-red-400 mb-4">
            <EyeOff className="w-6 h-6" />
            What We Never Do
          </h3>
          <ul className="space-y-3">
            {[
              'Screenshots or screen recording',
              'Keystroke logging',
              'Continuous GPS tracking',
              'Webcam monitoring',
              'Application surveillance'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-300">
                <X className="w-4 h-4 text-red-400" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Pricing Section
const Pricing = () => (
  <section id="pricing" className="py-20 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Simple, Transparent Pricing
        </h2>
        <p className="text-lg text-slate-600">
          Custom-built for your business needs
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-lg">
          <h3 className="text-xl font-semibold text-slate-900 mb-2">Build & Deploy</h3>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-4xl font-bold text-blue-600">₱100,000</span>
            <span className="text-slate-500">/month</span>
          </div>
          <p className="text-slate-600 mb-6">12-month development contract</p>
          <ul className="space-y-3">
            {[
              'Custom deployment',
              'All modules included',
              'Unlimited companies',
              'Training & onboarding',
              'Priority support'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-700">
                <CheckCircle className="w-5 h-5 text-green-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-lg">
          <h3 className="text-xl font-semibold text-slate-900 mb-2">Maintenance</h3>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-4xl font-bold text-blue-600">₱25,000</span>
            <span className="text-slate-500">/month</span>
          </div>
          <p className="text-slate-600 mb-6">After initial deployment</p>
          <ul className="space-y-3">
            {[
              'Bug fixes & updates',
              'Security patches',
              'Database backups',
              'Email support',
              '99.9% uptime SLA'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-700">
                <CheckCircle className="w-5 h-5 text-green-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-slate-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-slate-900">{faq.q}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-slate-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-500" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5 text-slate-600">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    headcount: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/demo-requests`, formData);
      setSubmitted(true);
      toast.success('Demo request submitted! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', company: '', headcount: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to transform your workforce management?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Get a personalized demo and see how Workforce OS can streamline your operations.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-cyan-400" />
                <span className="text-slate-300">sales@workforceos.com</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-6 h-6 text-cyan-400" />
                <span className="text-slate-300">+63 2 8888 WORK</span>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Thank you!</h3>
                <p className="text-slate-300">We'll contact you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Juan dela Cruz"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Work Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="juan@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name</label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Your Company Inc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Headcount</label>
                  <select
                    value={formData.headcount}
                    onChange={(e) => setFormData({ ...formData, headcount: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="" className="text-slate-900">Select...</option>
                    <option value="1-10" className="text-slate-900">1-10 employees</option>
                    <option value="11-50" className="text-slate-900">11-50 employees</option>
                    <option value="51-200" className="text-slate-900">51-200 employees</option>
                    <option value="201+" className="text-slate-900">201+ employees</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message (optional)</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Tell us about your needs..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Request Demo'}
                  <Send className="w-5 h-5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = ({ logoUrl, appName }) => (
  <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-xl" />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">{(appName || 'W')[0]}</span>
            </div>
          )}
          <span className="font-bold text-xl">{appName}</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-slate-400">
          <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="#contact" className="hover:text-white transition-colors">Contact</a>
        </div>
        <p className="text-sm text-slate-500">
          © 2026 {appName}. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main Landing Page Component
// Main Landing Page Component
export default function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [branding, setBranding] = useState({ logoUrl: null, appName: 'Workforce OS' });
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Fetch branding on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const [logoRes, nameRes] = await Promise.all([
          axios.get(`${API_URL}/api/settings/logo`),
          axios.get(`${API_URL}/api/settings/app-name`)
        ]);
        setBranding({
          logoUrl: logoRes.data.has_logo ? logoRes.data.logo_data : null,
          appName: nameRes.data.app_name || 'Workforce OS'
        });
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If user is logged in, optionally redirect to their dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      // Optional: Auto-redirect logged in users
      // Uncomment to enable auto-redirect
      // const dashboardPath = {
      //   owner: '/app/owner',
      //   admin: '/app/admin',
      //   manager: '/app/manager',
      //   employee: '/app/employee'
      // }[user.role] || '/app/employee';
      // navigate(dashboardPath);
    }
  }, [isAuthenticated, user, navigate]);

  return (
    <div className="min-h-screen">
      <Navbar isScrolled={isScrolled} logoUrl={branding.logoUrl} appName={branding.appName} />
      <Hero />
      <Features />
      <Modules />
      <Privacy />
      <Pricing />
      <FAQ />
      <Contact />
      <Footer logoUrl={branding.logoUrl} appName={branding.appName} />
    </div>
  );
}
